import type { DocumentContract } from '@protoss/doc';
import {
  filterExcludedSections,
  filterInactiveWhenBranches,
  iterateBodyBlocks,
  parseLogicalSections,
} from '@protoss/doc';
import type { SowRenderContext } from '@protoss/shared';
import { slugifyHeading } from '@protoss/shared';
import { SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import { DocContractView, type DocHeading } from '../../../components/doc';
import { resolveTokenText } from '../../../components/doc/previewSample';
import { useBrandPrimaryColor } from '../../../hooks/useBrandPrimaryColor';
import { salesApi } from '../services/salesApi';

export interface DealSowDocViewProps {
  dealUuid: string;
  /** Current SOW uuid - needed to persist data-table overrides. */
  sowUuid?: string;
  /**
   * Notifies the parent while a background refresh is in flight (cached content
   * stays on screen meanwhile), so it can surface a "Refreshing…" status. Not
   * called for the first load - that shows the skeleton instead.
   */
  onRefreshingChange?: (refreshing: boolean) => void;
}

/**
 * Last-resolved render context per deal (session-lived). This component only
 * mounts on the view route - the edit route shows the Tiptap editor instead - so
 * a view<->edit toggle remounts it and would otherwise re-fetch + flash a
 * skeleton every time. Seeding initial state from this cache lets the toggle show
 * the previous document immediately while a silent background refresh picks up
 * any edits. Self-heals on every mount (always refetches), so staleness is at
 * most one render behind.
 */
const renderContextCache = new Map<string, SowRenderContext>();

/**
 * Drop the cached render context for a deal so the next mount fetches fresh. Call after a SOW
 * regeneration: the cache otherwise seeds a stale document (the styled view wouldn't reflect the
 * new SOW until a page refresh).
 */
export function clearSowRenderContextCache(dealUuid: string): void {
  renderContextCache.delete(dealUuid);
}

/**
 * Read-only SOW renderer for the deal **view** route: resolves the document
 * contract (tokens, AI narrative, tables) into formatted prose with proper
 * heading hierarchy via {@link DocContractView}. Prose editing happens on the deal
 * edit route (Tiptap), but every resolved data table is inline cell-editable here
 * (and in the editor), stored as per-cell overrides that win at render.
 */
export function DealSowDocView({ dealUuid, sowUuid, onRefreshingChange }: DealSowDocViewProps) {
  // Workspace brand color so table headers/borders match the branded export.
  const brandColor = useBrandPrimaryColor();
  const [renderContext, setRenderContext] = React.useState<SowRenderContext | null>(
    () => renderContextCache.get(dealUuid) ?? null
  );
  // Skeleton only when there is nothing cached to show (true first load).
  const [loadingContext, setLoadingContext] = React.useState(
    () => !renderContextCache.has(dealUuid)
  );
  const [contextError, setContextError] = React.useState<string | null>(null);

  const loadRenderContext = React.useCallback(async () => {
    // First load (nothing cached) shows the skeleton; a re-entry with cached
    // content keeps it on screen and surfaces a lightweight "Refreshing…" status
    // instead of blanking (avoids the flash on a view<->edit toggle, which
    // remounts this component).
    const isBackgroundRefresh = renderContextCache.has(dealUuid);
    try {
      if (isBackgroundRefresh) onRefreshingChange?.(true);
      else setLoadingContext(true);
      setContextError(null);
      const ctx = await salesApi.getSowRenderContext(dealUuid);
      renderContextCache.set(dealUuid, ctx);
      setRenderContext(ctx);
    } catch (err: any) {
      // Both are 422s but the remedies differ: NOT_CONFIGURED needs a workspace admin
      // (no published export template for this category - docs/plans/29); PREREQ needs
      // the user to generate the estimate/resource plan.
      setContextError(
        err?.status === 422 && err?.message === 'EXPORT_TEMPLATE_NOT_CONFIGURED'
          ? 'NOT_CONFIGURED'
          : err?.status === 422
            ? 'PREREQ'
            : (err?.message ?? 'Failed to load SOW template')
      );
    } finally {
      setLoadingContext(false);
      onRefreshingChange?.(false);
    }
  }, [dealUuid, onRefreshingChange]);

  React.useEffect(() => {
    void loadRenderContext();
  }, [loadRenderContext]);

  // Clear the parent's "Refreshing…" status if we unmount mid-refresh (e.g. the
  // user toggles back to edit before the background fetch resolves).
  React.useEffect(() => () => onRefreshingChange?.(false), [onRefreshingChange]);

  const handleSaveOverride = React.useCallback(
    async (dataKey: string, rows: Array<Record<string, string>>) => {
      if (!sowUuid) return;
      await salesApi.saveSowDataOverride(dealUuid, sowUuid, dataKey, rows);
      await loadRenderContext();
    },
    [dealUuid, sowUuid, loadRenderContext]
  );

  const handleResetOverride = React.useCallback(
    async (dataKey: string) => {
      if (!sowUuid) return;
      await salesApi.resetSowDataOverride(dealUuid, sowUuid, dataKey);
      await loadRenderContext();
    },
    [dealUuid, sowUuid, loadRenderContext]
  );

  if (loadingContext) {
    return (
      <div className="deal-sow-doc-view deal-sow-doc-view--loading" aria-busy="true">
        {[0, 1, 2, 3].map((item) => (
          <SkeletonBlock key={item} className="deal-sow-doc-view__skeleton" />
        ))}
      </div>
    );
  }

  if (contextError === 'NOT_CONFIGURED') {
    // No published SOW export template matches this deal's category (422
    // EXPORT_TEMPLATE_NOT_CONFIGURED). Fixing it is a workspace-admin action, so say
    // that instead of going blank or hinting at estimate prerequisites.
    return (
      <div className="deal-sow-doc-view deal-sow-doc-view--empty">
        <p className="deal-sow-doc-view__empty-msg">
          SOW export is not configured for this deal&apos;s category yet - there is no published
          SOW export template for it. Ask your workspace admin to add one.
        </p>
      </div>
    );
  }

  if (contextError === 'PREREQ') {
    // Prerequisites missing (no saved estimate / resource plan) - the full document
    // (incl. the data tables) can't resolve yet. Tell the user instead of going blank.
    return (
      <div className="deal-sow-doc-view deal-sow-doc-view--empty">
        <p className="deal-sow-doc-view__empty-msg">
          Generate the estimate and resource plan to preview the full SOW (including the data
          tables) here.
        </p>
      </div>
    );
  }

  if (contextError || !renderContext) {
    return (
      <div className="deal-sow-doc-view deal-sow-doc-view--empty">
        <p className="deal-sow-doc-view__empty-msg">{contextError ?? 'Failed to load the SOW.'}</p>
      </div>
    );
  }

  // Every resolved data table is cell-editable now (override-wins); was limited to the 4
  // descriptive tables. Keyed off the resolved data arrays so only real tables are editable.
  const editableDataKeys = sowUuid
    ? new Set(Object.keys(renderContext.sample.data ?? {}))
    : undefined;
  const overriddenKeys = new Set(renderContext.dataOverrides ?? []);
  const driftKeys = new Set(renderContext.dataDrift ?? []);
  const tokenOverridden = new Set(renderContext.tokenOverridden ?? []);

  // Logical sections (heading + body). Drop the user-REMOVED conditional sections (and their
  // sub-sections) so they don't render in the read-only view (remove/restore lives in edit mode).
  const rawContract = renderContext.contract as unknown as DocumentContract;
  // Also drop the inactive branch of any `when`-gated section (e.g. the fixed-fee Fees block on a
  // time-and-materials deal) so the View shows exactly what the exported document will.
  const contract = filterExcludedSections(
    filterInactiveWhenBranches(rawContract, (path) => resolveTokenText(path, renderContext.sample)),
    new Set(renderContext.excludedSections ?? [])
  );
  const logicalSections = parseLogicalSections(contract).filter((s) => !s.isPreamble);

  // User-added sections: headings the user typed in the editor produce narrative slugs the
  // contract has no contentBlock for, so they'd otherwise never render. Find those orphaned
  // narrative slugs and append them after the template sections (heading humanized from the
  // slug, since the split drops the heading text; body resolved from sample.narrative).
  const consumedSlugs = new Set<string>([
    'development_summary',
    'overview',
    'scope_of_work',
    'client_background',
    'value_objectives',
    'assumptions',
    'source_document',
    // Sections a newer contract version dropped: older SOWs still store their prose, which must
    // not reappear here as if the user had added it.
    ...(rawContract.retiredNarrativeSlugs ?? []),
  ]);
  for (const section of contract.sections ?? []) {
    for (const child of iterateBodyBlocks((section.children ?? []) as Parameters<typeof iterateBodyBlocks>[0])) {
      const b = child as { type?: string; dataKey?: string };
      if (b.type === 'contentBlock' && typeof b.dataKey === 'string') {
        consumedSlugs.add(b.dataKey.split('.').pop() ?? b.dataKey);
      }
    }
  }
  // Slugs of the contract's own section HEADINGS (incl. the preamble/cover), with their tokens
  // resolved first so a token heading like `{{protoss:context.clientName}} · {{...projectName}}`
  // matches the deal's resolved title slug. A narrative slug matching one of these is legacy/stale
  // content for a section the contract already renders (e.g. the cover title, or a deal generated
  // with an older template whose `### Travel Expenses` split to `narrative.travel_expenses` while
  // the contract keys its blocks `travel_overview`/`travel_remote`/`travel_onsite`). Such slugs must
  // NOT be re-appended at the bottom - only genuinely user-added headings (not contract sections).
  const resolveHeadingTokens = (h: string): string =>
    h.replace(/\{\{\s*protoss:([^}]+?)\s*\}\}/g, (_m, p: string) =>
      resolveTokenText(p.trim(), renderContext.sample)
    );
  const sectionHeadingSlugs = new Set(
    parseLogicalSections(contract)
      .filter((s) => s.headingText)
      .map((s) => slugifyHeading(resolveHeadingTokens(s.headingText)))
  );
  const narrativeMap = (renderContext.sample.narrative ?? {}) as Record<string, string>;
  const extraSections = Object.keys(narrativeMap)
    .filter(
      (slug) =>
        !consumedSlugs.has(slug) &&
        !sectionHeadingSlugs.has(slug) &&
        !slug.endsWith('_ai') &&
        typeof narrativeMap[slug] === 'string' &&
        narrativeMap[slug].trim().length > 0
    )
    .map((slug) => ({
      slug,
      heading: slug.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    }));

  const headings: DocHeading[] = [
    ...logicalSections.map((s) => ({ text: s.headingText, level: s.headingLevel ?? 1 })),
    ...extraSections.map((s) => ({ text: s.heading, level: 1 })),
  ];

  return (
    <div className="deal-sow-doc-view">
      {/* Render contract sections (resolved heading + body via DocContractView). Removed
          conditional sections are already filtered out above; remove/restore lives in EDIT
          mode, not here in the read-only view. */}
      <div className="deal-sow-doc-view__content">
        {logicalSections.map((section) => (
          <div key={section.id} className="deal-sow-section">
            <DocContractView
              content={section.heading ? [section.heading, ...section.body] : section.body}
              mode="sample"
              sample={renderContext.sample}
              headings={headings}
              brandColor={brandColor ?? undefined}
              highlightGenerated
              editableDataKeys={editableDataKeys}
              overriddenKeys={overriddenKeys}
              driftKeys={driftKeys}
              tokenOverridden={tokenOverridden}
              onSaveOverride={handleSaveOverride}
              onResetOverride={handleResetOverride}
            />
          </div>
        ))}
        {/* User-added sections (not in the template) - rendered after the template sections. */}
        {extraSections.map((s) => (
          <div key={`extra-${s.slug}`} className="deal-sow-section">
            <DocContractView
              content={[
                { type: 'paragraph', heading: 'Heading1', children: [{ text: s.heading }] },
                { type: 'contentBlock', dataKey: `narrative.${s.slug}` },
              ] as unknown as Parameters<typeof DocContractView>[0]['content']}
              mode="sample"
              sample={renderContext.sample}
              headings={headings}
              brandColor={brandColor ?? undefined}
              highlightGenerated
              tokenOverridden={tokenOverridden}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
